/**
 * Mail-template API client (spec-36 §3.3 / PR A).
 *
 * Thin fetch wrappers around the backend endpoints that land in PR G. Until
 * the backend exists these calls resolve to 404, which is the intended state
 * for a contracts-only PR — the compose surface renders an empty template
 * picker. Mirrors the Supabase-bearer pattern from the other preinvest
 * feature clients.
 */

import { supabase } from '@/lib/supabase'
import type { MailMessage } from '@/features/preinvest/mail/types'
import type { MailRecipientSuggestion, MailTemplate, MailTemplateInput } from './types'

async function authHeaders(): Promise<Record<string, string>> {
  const {
    data: { session },
  } = await supabase.auth.getSession()

  const headers: Record<string, string> = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  }
  if (session?.access_token) {
    headers.Authorization = `Bearer ${session.access_token}`
  }
  return headers
}

async function fail(response: Response): Promise<never> {
  let message: string | null = null
  try {
    const body = (await response.json()) as { error?: unknown }
    if (typeof body.error === 'string') message = body.error
  } catch {
    // Non-JSON body — fall through to the generic message.
  }
  throw new Error(message ?? `Verzoek mislukt (${response.status})`)
}

/**
 * List mail templates (admin/team-only endpoint). Optional `status` filter
 * used by the compose dialog to fetch only definitieve templates.
 */
export async function listMailTemplates(
  status?: 'concept' | 'definitief',
): Promise<MailTemplate[]> {
  const url = status
    ? `/api/v1/preinvest/mail-templates?status=${status}`
    : '/api/v1/preinvest/mail-templates'
  const response = await fetch(url, { headers: await authHeaders() })
  if (!response.ok) return fail(response)
  return (await response.json()) as MailTemplate[]
}

/** Fetch one template (editor route). */
export async function getMailTemplate(templateId: string): Promise<MailTemplate> {
  const response = await fetch(`/api/v1/preinvest/mail-templates/${templateId}`, {
    headers: await authHeaders(),
  })
  if (!response.ok) return fail(response)
  return (await response.json()) as MailTemplate
}

/** Create a template (default `status='concept'`). */
export async function createMailTemplate(input: MailTemplateInput): Promise<MailTemplate> {
  const response = await fetch('/api/v1/preinvest/mail-templates', {
    method: 'POST',
    headers: await authHeaders(),
    body: JSON.stringify(input),
  })
  if (!response.ok) return fail(response)
  return (await response.json()) as MailTemplate
}

/** Patch a template. Cannot revert `definitief → concept` (PR G returns 409). */
export async function patchMailTemplate(
  templateId: string,
  patch: Partial<MailTemplateInput>,
): Promise<MailTemplate> {
  const response = await fetch(`/api/v1/preinvest/mail-templates/${templateId}`, {
    method: 'PATCH',
    headers: await authHeaders(),
    body: JSON.stringify(patch),
  })
  if (!response.ok) return fail(response)
  return (await response.json()) as MailTemplate
}

/** Soft-delete a template. Only `concept` rows are deletable. */
export async function deleteMailTemplate(templateId: string): Promise<void> {
  const response = await fetch(`/api/v1/preinvest/mail-templates/${templateId}`, {
    method: 'DELETE',
    headers: await authHeaders(),
  })
  if (!response.ok) return fail(response)
}

/**
 * Finalize a template (`concept → definitief`). Once finalized the template
 * becomes selectable from the compose dialog. 409 when already definitief.
 */
export async function finalizeMailTemplate(templateId: string): Promise<MailTemplate> {
  const response = await fetch(`/api/v1/preinvest/mail-templates/${templateId}/finalize`, {
    method: 'POST',
    headers: await authHeaders(),
  })
  if (!response.ok) return fail(response)
  return (await response.json()) as MailTemplate
}

/**
 * Unlock a template (`definitief → concept`) so its subject/body may be
 * edited. 409 when already concept. The backend logs the action.
 */
export async function unlockMailTemplate(templateId: string): Promise<MailTemplate> {
  const response = await fetch(`/api/v1/preinvest/mail-templates/${templateId}/unlock`, {
    method: 'POST',
    headers: await authHeaders(),
  })
  if (!response.ok) return fail(response)
  return (await response.json()) as MailTemplate
}

/**
 * Recipient suggestions for a deal: deal contacts + founders of the
 * participations (via `core.startup_persons role='founder'`). Compose dialog
 * shows them as chips.
 */
export async function listMailRecipients(
  dealId: string,
): Promise<MailRecipientSuggestion[]> {
  const response = await fetch(
    `/api/v1/preinvest/deals/${dealId}/mail/recipients`,
    { headers: await authHeaders() },
  )
  if (!response.ok) return fail(response)
  return (await response.json()) as MailRecipientSuggestion[]
}

/**
 * Payload for `POST /api/v1/preinvest/deals/{id}/mail/send-from-template`
 * (spec-36 PR G). The backend rejects concept templates with 409 and
 * substitutes `{{ key }}` merge-tags in subject + body from `variables`.
 * `bcc` is reserved for a future widening; PR G rejects it with 422.
 */
export interface MailSendFromTemplatePayload {
  template_id: string
  to: string[]
  cc?: string[]
  bcc?: string[]
  variables?: Record<string, string>
}

/**
 * Send a mail from a deal rendered from a `definitief` template. Kept in the
 * mail-templates feature so the compose dialog imports from one place; the
 * raw compose path (no template) still uses `mail/api.ts` `sendMessage`.
 */
export async function sendMailFromTemplate(
  dealId: string,
  payload: MailSendFromTemplatePayload,
): Promise<MailMessage> {
  const response = await fetch(
    `/api/v1/preinvest/deals/${dealId}/mail/send-from-template`,
    {
      method: 'POST',
      headers: await authHeaders(),
      body: JSON.stringify(payload),
    },
  )
  if (!response.ok) return fail(response)
  return (await response.json()) as MailMessage
}
